/* global globalThis */
import { useEffect, useState, useCallback } from "react";

/**
 * Custom hook to monitor network connectivity status
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableLogging - Enable console logging (default: false)
 * @returns {Object} Network status information
 * @returns {boolean} isOffline - Whether the device is currently offline
 * @returns {boolean} isOnline - Whether the device is currently online
 * @returns {boolean} wasOffline - Whether the device was offline previously (useful for "reconnected" messages)
 */
export default function useNetworkStatus(options = {}) {
  const { enableLogging = false } = options;

  const [isOffline, setIsOffline] = useState(
    typeof globalThis?.navigator === 'undefined' 
      ? false 
      : !globalThis.navigator.onLine
  );
  const [wasOffline, setWasOffline] = useState(false);

  const handleOffline = useCallback(() => {
    if (enableLogging) {
      console.log("[Network Status] Network connection lost - device is offline");
    }
    setIsOffline(true);
    setWasOffline(true);
  }, [enableLogging]);

  const handleOnline = useCallback(() => {
    if (enableLogging) {
      console.log("[Network Status] Network connection restored - device is online");
    }
    setIsOffline(false);
  }, [enableLogging]);

  useEffect(() => {
    if (typeof globalThis?.addEventListener !== 'function') {
      return;
    }

    globalThis.addEventListener("offline", handleOffline, { passive: true });
    globalThis.addEventListener("online", handleOnline, { passive: true });

    return () => {
      globalThis.removeEventListener("offline", handleOffline);
      globalThis.removeEventListener("online", handleOnline);
    };
  }, [handleOffline, handleOnline]);

  return { 
    isOffline, 
    isOnline: !isOffline,
    wasOffline 
  };
}